import { useEquipment } from '@services/common'
import { useCreateEquipment } from '@services/common/useCreateEquipment'
import { usePatchEquipment } from '@services/common/usePatchEquipment'
import { useUpdateRoom } from '@services/flats/mutations/useUpdateRoom'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CatalogMultiSelect } from '@/components/ui/catalog-multi-select'
import { CatalogRenameDialog } from '@/components/ui/catalog-rename-dialog'
import { ComboboxOption } from '@/components/ui/combobox'

interface Props {
  roomId: string
  roomTypeId: string
  initial: { id: string; name: string }[]
}

type CatalogItem = {
  id?: string
  name?: string | null
  name_sk?: string | null
  name_en?: string | null
}
type RichOption = ComboboxOption & { nameSk?: string; nameEn?: string }

/**
 * Equipment multi-select for a single room. Auto-saves the selection to the
 * room ~700ms after the last change, and lets admins create/rename equipment
 * catalog items inline.
 */
export function RoomEquipmentField({ roomId, roomTypeId, initial }: Props) {
  const { t } = useTranslation('flat')
  const { mutate: updateRoom } = useUpdateRoom()
  const { mutate: createEquipment } = useCreateEquipment()
  const { mutate: patchEquipment, isPending: patching } = usePatchEquipment()

  const [value, setValue] = useState<string[]>(initial.map((e) => e.id))
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [options, setOptions] = useState<RichOption[]>(
    initial.map((e) => ({ value: e.id, label: e.name }))
  )
  const [rename, setRename] = useState<{
    id: string
    nameSk: string
    nameEn: string
  } | null>(null)
  const skipSave = useRef(true)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  const { data: equipment, isLoading } = useEquipment({ search: debouncedSearch })

  useEffect(() => {
    if (!equipment) return
    // Upsert by id so labels re-localize and name_sk/name_en stay in sync.
    setOptions((prev) => {
      const map = new Map(prev.map((o) => [o.value, o]))
      ;(equipment as CatalogItem[]).forEach((e) => {
        if (!e.id) return
        map.set(e.id, {
          value: e.id,
          label: e.name ?? e.name_sk ?? '',
          nameSk: e.name_sk ?? undefined,
          nameEn: e.name_en ?? undefined
        })
      })
      return Array.from(map.values())
    })
  }, [equipment])

  // Auto-save the selection (debounced), skipping the initial mount.
  useEffect(() => {
    if (skipSave.current) {
      skipSave.current = false
      return
    }
    const timer = setTimeout(() => {
      updateRoom({ id: roomId, equipment: value, room_type: roomTypeId })
    }, 2000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const handleCreate = (name: string) =>
    createEquipment(
      { body: { name } },
      {
        onSuccess: (created: CatalogItem) => {
          if (!created.id) return
          setOptions((prev) => [
            ...prev,
            { value: created.id!, label: created.name ?? name }
          ])
          setValue((prev) => [...prev, created.id!])
        }
      }
    )

  const saveRename = (nameSk: string, nameEn: string) => {
    if (!rename) return
    patchEquipment(
      { id: rename.id, body: { name: nameSk, name_sk: nameSk, name_en: nameEn } },
      {
        onSuccess: () => {
          setOptions((prev) =>
            prev.map((o) =>
              o.value === rename.id
                ? { ...o, label: nameSk, nameSk, nameEn }
                : o
            )
          )
          setRename(null)
        }
      }
    )
  }

  return (
    <>
      <CatalogMultiSelect
        value={value}
        onChange={setValue}
        onSearch={setSearch}
        options={options}
        loading={isLoading}
        placeholder={t('checklist.select_equipment')}
        onCreate={handleCreate}
        onEdit={(o) =>
          setRename({
            id: o.value,
            nameSk: (o as RichOption).nameSk ?? o.label,
            nameEn: (o as RichOption).nameEn ?? ''
          })
        }
      />

      <CatalogRenameDialog
        open={rename !== null}
        title={t('checklist.rename_title')}
        labelSk={t('checklist.name_sk')}
        labelEn={t('checklist.name_en')}
        initialSk={rename?.nameSk ?? ''}
        initialEn={rename?.nameEn ?? ''}
        cancelLabel={t('checklist.no')}
        saveLabel={t('checklist.save')}
        pending={patching}
        onClose={() => setRename(null)}
        onSave={saveRename}
      />
    </>
  )
}
